import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScoreIcon from '@mui/icons-material/Score';
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ConfirmDialog from "../../admin/components/ConfirmDialog";
import CustomSnackbar from "../../admin/components/CustomSnackbar";
import MiniPuzRow from "../../admin/pzl_common/MiniPuzRow";
import VerifyDialog from "../../admin/pzl_common/VerifyDialog";
import ResolvePuzzleDialog from "../../admin/pzl_common/ResolvePuzzleDialog";
import { DIRECTION_DIAGONAL_LEFT_UP, DIRECTION_DIAGONAL_RIGHT_UP, DIRECTION_HORIZONTAL, DIRECTION_VERTICAL } from "../../admin/pzl_common/PuzzleUtil";
import WordMeaning from "../../admin/pzl_common/WordMeaning";
import WordRedefine from "../../admin/pzl_common/WordRedefine";
import BorderHeader from "../../admin/pzl_common/BorderHeader";
import WordNetworkDialog from "../../admin/pzl_common/WordNetworkDialog";
import WordScoreDialog from "../../admin/pzl_common/WordScoreDialog";
import AdminGridBoardService, { DRAFT, PUBLISHED, READY_TO_REVIEW, REVIEWED } from "../../admin/pzl_grid/board/AdminGridBoardService";
import Logger from "../../services/Logger";
import { usePuzzleData } from "../../admin/pzl_grid/board/usePuzzleData";
import CreatorGridService from "../service/CreatorGridService";
import UserService from "../../services/UserService";
import GridCanAssoc from "../../admin/pzl_grid/board/candidates/GridCanAssoc";
import GridCandManual from "../../admin/pzl_grid/board/candidates/GridCandManual";
import GridCandAI from "../../admin/pzl_grid/board/candidates/GridCandAI";
import { generatePositions, getGridWord } from "../../admin/pzl_grid/board/candidates/GridCandUtils";
import GridFillDialog from "../../admin/pzl_grid/board/candidates/GridFillDialog";
import GridFillNotInUsePanel from "../../admin/pzl_grid/board/candidates/GridFillNotInUsePanel";
import AddBannedWordDialog from "../../admin/setting/banned_words/AddBannedWordDialog";
import BannedService from "../../admin/setting/banned_words/BannedService";
import BannedWordsPopup from "../../admin/setting/banned_words/BannedWordsPopup";
import ApiService from "../../services/ApiService";
import MarkAsReviewedDialog from "../common/MarkAsReviewedDialog";
import ValidationDialog from "../common/ValidationDialog";
import { trackEvent } from "../../services/GoogleAnalytics";

const normalizeFrenchWord = (word) => {
    if (!word || typeof word !== 'string') return word;
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
};

export default function CreatorGridEditPage() {
    const { gridId } = useParams();
    const navigate = useNavigate();
    const [refresh, setRefresh] = useState(0);
    const [isManager, setIsManager] = useState(false);
    const [isCreator, setIsCreator] = useState(false);
    const { hwTpl: gridTpl, loading, error: fetchError } = usePuzzleData(gridId, refresh);

    useEffect(() => {
        trackEvent("entered", { name: "CreatorGridEditPage" });
    }, []);

    useEffect(() => {
        let cancelled = false;
        UserService.getUserInfo().then((u) => {
            const role = u?.cloudUser?.userRole;
            if (!cancelled && role) {
                const r = String(role).toUpperCase();
                setIsManager(r === "MANAGER");
                setIsCreator(r === "CREATOR");
            }
        }).catch(() => { });
        return () => { cancelled = true; };
    }, []);

    const [selectedWord, setSelectedWord] = useState(null);
    const [selectedAssociatedWord, setSelectedAssociatedWord] = useState(null);
    const [selectedNetworkWord, setSelectedNetworkWord] = useState(null);
    const abortControllerRef = useRef(null);
    const gridContainerRef = useRef(null);
    const gridContentRef = useRef(null);
    const [gridScale] = useState(1);

    const modeRef = useRef('assoc');
    const [, setMode] = useState(() => modeRef.current);

    const [selectedManualWord, setSelectedManualWord] = useState(null);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const [disableDialogOpen, setDisableDialogOpen] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);
    const [puzzleTypeDialogOpen, setPuzzleTypeDialogOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [resolveDialogType, setResolveDialogType] = useState("duplicates");

    const [loadingApplyTitleHook, setLoadingApplyTitleHook] = useState(false);
    const [loadingFillAdd, setLoadingFillAdd] = useState(false);
    const [fillDialogOpen, setFillDialogOpen] = useState(false);
    const [placementWordFromFill, setPlacementWordFromFill] = useState(null);
    const [showTitleHookDialog, setShowTitleHookDialog] = useState(false);

    const [reviewedDialogOpen, setReviewedDialogOpen] = useState(false);
    const [loadingReviewed, setLoadingReviewed] = useState(false);
    const [reviewedValidationLoading, setReviewedValidationLoading] = useState(false);
    const [reviewedDuplicateWords, setReviewedDuplicateWords] = useState([]);
    const [reviewedBannedWords, setReviewedBannedWords] = useState([]);
    const [draftDialogOpen, setDraftDialogOpen] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(false);
    const [readyToReviewDialogOpen, setReadyToReviewDialogOpen] = useState(false);
    const [loadingReadyToReview, setLoadingReadyToReview] = useState(false);
    const [publishDialogOpen, setPublishDialogOpen] = useState(false);
    const [loadingPublished, setLoadingPublished] = useState(false);
    const [backToReviewedDialogOpen, setBackToReviewedDialogOpen] = useState(false);
    const [loadingBackToReviewed, setLoadingBackToReviewed] = useState(false);

    const [openWordRedefineDialog, setOpenWordRedefineDialog] = useState(false);
    const [editingDefinition, setEditingDefinition] = useState("");
    const [editingWord, setEditingWord] = useState("");

    const [openBannedWordsPopup, setOpenBannedWordsPopup] = useState(false);
    const [openAddBannedWordDialog, setOpenAddBannedWordDialog] = useState(false);
    const [selectedWordForBanned, setSelectedWordForBanned] = useState("");
    const [loadingAddBanned, setLoadingAddBanned] = useState(false);
    const [networkDialogOpen, setNetworkDialogOpen] = useState(false);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
    const [wordScores, setWordScores] = useState([]);
    const [loadingScores, setLoadingScores] = useState(false);
    const [loadingRearrange, setLoadingRearrange] = useState(false);
    const [showAllWordsColored, setShowAllWordsColored] = useState(false);

    const [square, setSquare] = useState([]);

    const filledCoordinates = useMemo(() => {
        return gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
    }, [gridTpl?.filledCoordinates]);

    const scatteredWord = useMemo(() => {
        return filledCoordinates.find(coord => coord.direction === "scattered");
    }, [filledCoordinates]);

    useEffect(() => {
        if (filledCoordinates.length > 0) {
            setSquare(initializeSquare(filledCoordinates));
        } else {
            setSquare(initializeSquare([]));
        }
    }, [filledCoordinates]);

    const initializeSquare = (filledCoordinates) => {
        const size = 12;
        let newSquare = Array.from({ length: size }, () => Array(size).fill(0));
        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                newSquare[row][col] = findCharacter(row, col, filledCoordinates);
            }
        }
        return newSquare;
    };

    const findCharacter = (row, col, filledCoordinates) => {
        let found = "";
        if (filledCoordinates) {
            found = filledCoordinates
                .map((c) => c.positions)
                .map((positions) => {
                    const matchingPosition = positions.find((p) => p.row === row && p.col === col);
                    if (matchingPosition && matchingPosition.value) {
                        const char = matchingPosition.value;
                        const normalized = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                        return normalized.toUpperCase();
                    }
                    return "";
                })
                .find((value) => value !== "");
        }
        return found || "";
    };

    const hasWordNetwork = useMemo(() => {
        const wordNetwork = gridTpl?.template?.wordNetwork || gridTpl?.wordNetwork;
        return wordNetwork?.nodes && wordNetwork?.links;
    }, [gridTpl]);

    const wordNetworkData = useMemo(() => {
        return gridTpl?.template?.wordNetwork || gridTpl?.wordNetwork;
    }, [gridTpl]);

    const GRID_TOTAL_CELLS = 144;
    const { cellsFullyFilled, emptyCellCount } = useMemo(() => {
        const uniqueCells = new Set();
        filledCoordinates.forEach(word => {
            if (word.text && word.positions) {
                word.positions.forEach(pos => { uniqueCells.add(`${pos.row}-${pos.col}`); });
            }
        });
        return {
            cellsFullyFilled: uniqueCells.size >= GRID_TOTAL_CELLS,
            emptyCellCount: Math.max(0, GRID_TOTAL_CELLS - uniqueCells.size)
        };
    }, [filledCoordinates]);

    const WORD_COLOR_PALETTE = [
        "#ef5350", "#66bb6a", "#42a5f5", "#ab47bc", "#ffca28", "#ff7043",
        "#26a69a", "#7e57c2", "#ec407a", "#5c6bc0", "#9ccc65", "#ffa726",
        "#ef9a9a", "#81c784", "#64b5f6", "#ce93d8"
    ];
    const allWordsPositionColors = useMemo(() => {
        if (!showAllWordsColored || !filledCoordinates?.length) return null;
        const words = filledCoordinates
            .filter((c) => c.text && typeof c.text === "string" && c.direction !== "scattered")
            .map((c) => ({ positions: c.positions || [] }));
        const positionColors = {};
        words.forEach((w, idx) => {
            const color = WORD_COLOR_PALETTE[idx % WORD_COLOR_PALETTE.length];
            (w.positions || []).forEach((p) => {
                if (p != null && typeof p.row === "number" && typeof p.col === "number") {
                    positionColors[`${p.row}-${p.col}`] = color;
                }
            });
        });
        return Object.keys(positionColors).length > 0 ? positionColors : null;
    }, [filledCoordinates, showAllWordsColored]);

    useEffect(() => {
        setMode(modeRef.current);
    }, [gridTpl?.id]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleSnackbar = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleWordSelect = async (candidate) => {
        if (!candidate) return;
        setSelectedNetworkWord(null);
        const normalizedCandidate = {
            ...candidate,
            word: candidate.word || candidate.text || "",
            positions: candidate.positions && Array.isArray(candidate.positions) ? candidate.positions : []
        };
        if (normalizedCandidate.positions?.length > 0 && normalizedCandidate.word &&
            (normalizedCandidate.startRow !== undefined || normalizedCandidate.startCol !== undefined || normalizedCandidate.direction || normalizedCandidate.pattern)) {
            setSelectedAssociatedWord(normalizedCandidate);
            setSelectedWord(null);
        } else {
            setSelectedWord(normalizedCandidate);
            setSelectedAssociatedWord(null);
        }
    };

    const handleWordAdd = async (candidate) => {
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        try {
            if (!gridTpl?.filledCoordinates) {
                handleSnackbar("No puzzle data available", "error");
                return;
            }
            if (!candidate) {
                handleSnackbar("Invalid word candidate", "error");
                return;
            }
            let wordText = candidate.word || candidate.text;
            if (!wordText) {
                handleSnackbar("Word candidate has no text content", "error");
                return;
            }
            const language = gridTpl?.language || 'english';
            wordText = language === 'french' ? normalizeFrenchWord(wordText) : wordText.toLowerCase();

            const filledCoordinates = JSON.parse(gridTpl.filledCoordinates);
            const isReversed = candidate.isReversed || false;
            const newWordEntry = {
                id: uuidv4(),
                text: wordText,
                direction: candidate.direction,
                length: wordText.length,
                reversed: isReversed,
                positions: candidate.positions.map((pos, index) => {
                    if (pos.value !== undefined && pos.value !== "" && pos.value !== null) {
                        return { ...pos, value: pos.value.toLowerCase() };
                    }
                    const isDiagonal = candidate.direction === DIRECTION_DIAGONAL_RIGHT_UP || candidate.direction === DIRECTION_DIAGONAL_LEFT_UP;
                    const charIndex = isReversed ? (isDiagonal ? index : (wordText.length - 1 - index)) : index;
                    const charValue = (charIndex >= 0 && charIndex < wordText.length) ? wordText[charIndex] : (pos.value || "");
                    return { ...pos, value: charValue.toLowerCase() };
                })
            };
            const updatedCoordinates = [...filledCoordinates, newWordEntry];
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedCoordinates, wordText);
            handleSnackbar(`Word "${wordText}" added successfully!`, "success");
            setRefresh(prev => prev + 1);
        } catch (error) {
            Logger.error("Error adding new word:", error);
            handleSnackbar("Error adding new word", "error");
        } finally {
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleBack = () => {
        navigate('/creator/grid');
    };

    const handleModeChange = (_event, newMode) => {
        if (newMode !== null) {
            modeRef.current = newMode;
            setMode(newMode);
            setSelectedWord(null);
            setSelectedAssociatedWord(null);
            setSelectedManualWord(null);
            setSelectedNetworkWord(null);
        }
    };

    const handleManualWordSelect = async (candidate) => {
        setSelectedNetworkWord(null);
        setSelectedManualWord(candidate);
    };

    const handleManualWordAdd = async (candidate) => {
        try {
            if (!candidate) {
                handleSnackbar("Invalid manual word candidate", "error");
                return;
            }
            let wordText = candidate.gridWord || candidate.word || candidate.text;
            if (!wordText) {
                handleSnackbar("Manual word candidate has no text content", "error");
                return;
            }
            wordText = wordText.replace(/-/g, '');
            const language = gridTpl?.language || 'english';
            wordText = language === 'french' ? normalizeFrenchWord(wordText) : wordText.toLowerCase();

            const isReversed = candidate.isReversed || false;
            const newWordEntry = {
                id: uuidv4(),
                text: wordText,
                direction: candidate.direction,
                length: wordText.length,
                positions: candidate.positions.map((pos, index) => {
                    if (pos.value !== undefined && pos.value !== "" && pos.value !== null) {
                        return { ...pos, value: pos.value.toLowerCase() };
                    }
                    const isDiagonal = candidate.direction === DIRECTION_DIAGONAL_RIGHT_UP || candidate.direction === DIRECTION_DIAGONAL_LEFT_UP;
                    const charIndex = isReversed ? (isDiagonal ? index : (wordText.length - 1 - index)) : index;
                    const charValue = (charIndex >= 0 && charIndex < wordText.length) ? wordText[charIndex] : (pos.value || "");
                    return { ...pos, value: charValue.toLowerCase() };
                })
            };
            const updatedFilledCoordinates = [...(gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : []), newWordEntry];
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, newWordEntry.text);
            handleSnackbar(`Word "${wordText}" added successfully!`, "success");
            setRefresh(prev => prev + 1);
            setSelectedManualWord(null);
        } catch (error) {
            Logger.error("Error adding manual word:", error);
            handleSnackbar("Error adding word. Please try again.", "error");
        }
    };

    const handleCellClick = (row, col) => {
        if (modeRef.current === 'manual' || modeRef.current === 'ai') {
            if (selectedManualWord) {
                const gridWord = selectedManualWord.gridWord || selectedManualWord.word?.replace(/-/g, '') || '';
                const updatedWord = {
                    ...selectedManualWord,
                    startRow: row,
                    startCol: col,
                    positions: generatePositions(row, col, selectedManualWord.direction, gridWord.length, gridWord, selectedManualWord.isReversed)
                };
                setSelectedManualWord(updatedWord);
            } else {
                const tempWord = { startRow: row, startCol: col, direction: DIRECTION_HORIZONTAL, isReversed: false };
                setSelectedManualWord(tempWord);
                setTimeout(() => {
                    setSelectedManualWord(prev => (prev && !prev.word ? null : prev));
                }, 100);
            }
        }
    };

    const handleDiscardPuzzle = async () => {
        try {
            await CreatorGridService.updateGridState(gridTpl.id, "DISCARDED");
            handleSnackbar("Puzzle discarded successfully", "success");
            navigate('/creator/grid');
        } catch (error) {
            Logger.error("Error discarding puzzle:", error);
            handleSnackbar("Error discarding puzzle", "error");
        }
    };

    const handleDisablePuzzle = async () => {
        try {
            await CreatorGridService.updateGridState(gridTpl.id, "DISABLED");
            handleSnackbar("Puzzle disabled successfully", "success");
            navigate('/creator/grid');
        } catch (error) {
            Logger.error("Error disabling puzzle:", error);
            handleSnackbar("Error disabling puzzle", "error");
        }
    };

    const handleDeleteWordFromNetwork = async (wordText) => {
        if (!wordText) return;
        setWordToDelete(wordText);
        setDiscardDialogOpen(true);
    };

    const handleConfirmDeleteWord = async () => {
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        try {
            if (!wordToDelete) return;
            const filledCoords = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            const updatedCoords = filledCoords.filter(coord =>
                coord.text && coord.text.toUpperCase() !== wordToDelete?.toUpperCase()
            );
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedCoords, null);
            handleSnackbar(`Word "${wordToDelete}" removed successfully`, "success");
            setRefresh(prev => prev + 1);
            setWordToDelete(null);
            setDiscardDialogOpen(false);
        } catch (error) {
            Logger.error("Error deleting word:", error);
            handleSnackbar("Error deleting word", "error");
            setDiscardDialogOpen(false);
            setWordToDelete(null);
        } finally {
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleApplyTitleHook = () => setShowTitleHookDialog(true);

    const confirmApplyTitleHook = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingApplyTitleHook(true);
            const result = await CreatorGridService.applyTitleHook(gridTpl.id);
            if (result?.code === "FAILURE") {
                handleSnackbar(result.message || "Failed to apply title hook", "error");
            } else {
                handleSnackbar("Title hook applied successfully", "success");
                setRefresh(prev => prev + 1);
            }
        } catch (err) {
            Logger.error("Error applying title hook:", err);
            handleSnackbar("Failed to apply title hook. Please try again.", "error");
        } finally {
            setLoadingApplyTitleHook(false);
        }
    };

    const mapApiDirectionToGrid = (dir) => {
        if (!dir) return DIRECTION_HORIZONTAL;
        const d = String(dir).toLowerCase();
        if (d === "vertical" || d === "down") return DIRECTION_VERTICAL;
        if (d === "horizontal" || d === "right" || d === "left") return DIRECTION_HORIZONTAL;
        if (d === "diagonal-right-up") return DIRECTION_DIAGONAL_RIGHT_UP;
        if (d === "diagonal-left-up") return DIRECTION_DIAGONAL_LEFT_UP;
        return DIRECTION_HORIZONTAL;
    };

    const inferMatchKeywordDirection = (positions) => {
        if (!Array.isArray(positions) || positions.length <= 1) return DIRECTION_HORIZONTAL;
        const sorted = [...positions].sort((a, b) => (a.row !== b.row ? a.row - b.row : a.col - b.col));
        const sameRow = sorted.every((p) => p.row === sorted[0].row);
        const sameCol = sorted.every((p) => p.col === sorted[0].col);
        if (sameRow) return DIRECTION_HORIZONTAL;
        if (sameCol) return DIRECTION_VERTICAL;
        const dRow = sorted[1].row - sorted[0].row;
        const dCol = sorted[1].col - sorted[0].col;
        if (dRow === dCol) return DIRECTION_DIAGONAL_LEFT_UP;
        if (dRow === -dCol) return DIRECTION_DIAGONAL_RIGHT_UP;
        return DIRECTION_HORIZONTAL;
    };

    const getExistingCellMap = (filledCoords) => {
        const map = new Map();
        if (!filledCoords || !Array.isArray(filledCoords)) return map;
        filledCoords.forEach((entry) => {
            (entry.positions || []).forEach((p) => {
                if (p.row != null && p.col != null && (p.value || "").trim() !== "") {
                    const key = `${p.row}-${p.col}`;
                    const normalized = normalizeFrenchWord((p.value || "").trim());
                    if (normalized) map.set(key, { raw: (p.value || "").trim(), normalized });
                }
            });
        });
        return map;
    };

    const handleFillPlaceWord = async (item) => {
        if (!gridTpl?.id || !item?.word) return;
        setLoadingFillAdd(true);
        try {
            const coords = item.coordinates;
            const displayWord = (item.word || coords?.text || coords?.word || "").trim();
            const gridWord = getGridWord(displayWord.toLowerCase());
            let direction;
            let positions;
            let reversed = false;
            if (coords && Array.isArray(coords.positions) && coords.positions.length > 0) {
                direction = mapApiDirectionToGrid(coords.direction);
                reversed = coords.reversed ?? false;
                const raw = coords.positions.slice(0, gridWord.length);
                positions = raw.map((p, i) => {
                    const charIndex = reversed ? gridWord.length - 1 - i : i;
                    return { row: p.row, col: p.col, value: (gridWord[charIndex] ?? "").toLowerCase() };
                });
            } else {
                const rawPositions = item.positions || [];
                direction = inferMatchKeywordDirection(rawPositions);
                let startPos = null;
                if (rawPositions.length > 0) {
                    const sorted = [...rawPositions].filter((p) => p.row != null && p.col != null);
                    if (direction === DIRECTION_HORIZONTAL) startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                    else if (direction === DIRECTION_VERTICAL) startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                    else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                    else if (direction === DIRECTION_DIAGONAL_LEFT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                    else startPos = sorted[0];
                }
                positions = startPos
                    ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false)
                    : rawPositions.filter((p) => (p.letter || p.value || "").trim() !== "").sort((a, b) => (direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP ? a.col - b.col : a.row - b.row)).slice(0, gridWord.length).map((p, i) => ({ row: p.row, col: p.col, value: gridWord[i] || "" }));
            }
            const existingFilled = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            const existingCellMap = getExistingCellMap(existingFilled);
            const conflicts = [];
            (positions || []).forEach((p) => {
                const key = `${p.row}-${p.col}`;
                const newVal = (p.value || "").trim();
                const newNormalized = normalizeFrenchWord(newVal);
                if (!newNormalized) return;
                const existing = existingCellMap.get(key);
                if (existing && existing.normalized !== newNormalized) {
                    conflicts.push({ row: p.row, col: p.col, existing: existing.raw, new: newVal });
                }
            });
            if (conflicts.length > 0) {
                const msg = conflicts
                    .map((c) => `(${c.row + 1},${c.col + 1}) has '${c.existing}' but new word has '${c.new}'`)
                    .join("; ");
                handleSnackbar(msg, "error");
                setLoadingFillAdd(false);
                return;
            }
            const newWordEntry = { id: uuidv4(), text: displayWord, direction, length: gridWord.length, reversed, positions };
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, [...existingFilled, newWordEntry], gridWord);
            handleSnackbar(`Word "${displayWord}" added successfully!`, "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding word from Fill dialog:", err);
            handleSnackbar(err?.message || "Failed to add word. Please try again.", "error");
        } finally {
            setLoadingFillAdd(false);
        }
    };

    const handleFillTab2Add = async (item) => {
        if (!gridTpl?.id || !item?.word) return;
        setLoadingFillAdd(true);
        try {
            const displayWord = (item.word || "").trim();
            const gridWord = getGridWord(displayWord.toLowerCase());
            const direction = mapApiDirectionToGrid(item.direction);
            const rawPositions = item.positions || [];
            let startPos = null;
            if (rawPositions.length > 0) {
                const sorted = [...rawPositions].filter((p) => p.row != null && p.col != null);
                if (direction === DIRECTION_HORIZONTAL) startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                else if (direction === DIRECTION_VERTICAL) startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                else if (direction === DIRECTION_DIAGONAL_LEFT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                else startPos = sorted[0];
            }
            const positions = startPos ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false) : rawPositions.filter((p) => (p.letter || p.value || "").trim() !== "").sort((a, b) => (direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP ? a.col - b.col : a.row - b.row)).slice(0, gridWord.length).map((p, i) => ({ row: p.row, col: p.col, value: gridWord[i] || "" }));
            const newWordEntry = { id: uuidv4(), text: displayWord, direction, length: gridWord.length, reversed: false, positions };
            const existingFilled = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, [...existingFilled, newWordEntry], gridWord);
            handleSnackbar(`Word "${displayWord}" added successfully!`, "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding candidate word from Fill dialog:", err);
            handleSnackbar(err?.message || "Failed to add word. Please try again.", "error");
        } finally {
            setLoadingFillAdd(false);
        }
    };

    /** Place all cart words in one batch (accumulated coordinates so each save includes previous words). Works for all 3 tabs. */
    const handlePlaceWords = async (items) => {
        if (!gridTpl?.id || !Array.isArray(items) || items.length === 0) return;
        setLoadingFillAdd(true);
        try {
            const existingFilled = gridTpl?.filledCoordinates ? JSON.parse(gridTpl.filledCoordinates) : [];
            let filledSoFar = [...existingFilled];
            const addedWords = [];
            let skippedNoPositions = 0;

            for (const item of items) {
                const coords = item.coordinates;
                const displayWord = (item.word ?? item.text ?? coords?.text ?? coords?.word ?? "").toString().trim();
                if (!displayWord) continue;
                const gridWord = getGridWord(displayWord.toLowerCase());
                const fromCoords = coords && Array.isArray(coords.positions) && coords.positions.length > 0 ? coords.positions : [];
                const fromTop = Array.isArray(item.positions) && item.positions.length > 0 ? item.positions : [];
                const rawPositions = fromCoords.length > 0 ? fromCoords : fromTop;
                if (rawPositions.length === 0) {
                    skippedNoPositions += 1;
                    continue;
                }

                let direction;
                let positions;
                let reversed = false;
                if (coords && Array.isArray(coords.positions) && coords.positions.length > 0) {
                    direction = mapApiDirectionToGrid(coords.direction);
                    reversed = coords.reversed ?? false;
                    const raw = rawPositions.slice(0, gridWord.length);
                    positions = raw.map((p, i) => {
                        const charIndex = reversed ? gridWord.length - 1 - i : i;
                        return { row: p.row, col: p.col, value: (gridWord[charIndex] ?? "").toLowerCase() };
                    });
                } else if (item.direction != null) {
                    direction = mapApiDirectionToGrid(item.direction);
                    const sorted = [...rawPositions].filter((p) => p.row != null && p.col != null);
                    let startPos = null;
                    if (sorted.length > 0) {
                        if (direction === DIRECTION_HORIZONTAL) startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                        else if (direction === DIRECTION_VERTICAL) startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                        else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                        else if (direction === DIRECTION_DIAGONAL_LEFT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                        else startPos = sorted[0];
                    }
                    positions = startPos ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false) : rawPositions.filter((p) => (p.letter || p.value || "").trim() !== "").sort((a, b) => (direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP ? a.col - b.col : a.row - b.row)).slice(0, gridWord.length).map((p, i) => ({ row: p.row, col: p.col, value: gridWord[i] || "" }));
                } else {
                    direction = inferMatchKeywordDirection(rawPositions);
                    let startPos = null;
                    const sorted = [...rawPositions].filter((p) => p.row != null && p.col != null);
                    if (sorted.length > 0) {
                        if (direction === DIRECTION_HORIZONTAL) startPos = sorted.reduce((a, b) => (a.col <= b.col ? a : b));
                        else if (direction === DIRECTION_VERTICAL) startPos = sorted.reduce((a, b) => (a.row <= b.row ? a : b));
                        else if (direction === DIRECTION_DIAGONAL_RIGHT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col >= b.col ? a : b))));
                        else if (direction === DIRECTION_DIAGONAL_LEFT_UP) startPos = sorted.reduce((a, b) => (a.row < b.row ? a : (a.row > b.row ? b : (a.col <= b.col ? a : b))));
                        else startPos = sorted[0];
                    }
                    positions = startPos ? generatePositions(startPos.row, startPos.col, direction, gridWord.length, gridWord, false) : rawPositions.filter((p) => (p.letter || p.value || "").trim() !== "").sort((a, b) => (direction === DIRECTION_HORIZONTAL || direction === DIRECTION_DIAGONAL_RIGHT_UP || direction === DIRECTION_DIAGONAL_LEFT_UP ? a.col - b.col : a.row - b.row)).slice(0, gridWord.length).map((p, i) => ({ row: p.row, col: p.col, value: gridWord[i] || "" }));
                }

                if (!positions || positions.length === 0) {
                    skippedNoPositions += 1;
                    continue;
                }
                const existingCellMap = getExistingCellMap(filledSoFar);
                const conflicts = [];
                (positions || []).forEach((p) => {
                    const key = `${p.row}-${p.col}`;
                    const newVal = (p.value || "").trim();
                    const newNormalized = normalizeFrenchWord(newVal);
                    if (!newNormalized) return;
                    const existing = existingCellMap.get(key);
                    if (existing && existing.normalized !== newNormalized) {
                        conflicts.push({ row: p.row, col: p.col, existing: existing.raw, new: newVal });
                    }
                });
                if (conflicts.length > 0) {
                    const msg = `"${displayWord}": ${conflicts.map((c) => `(${c.row + 1},${c.col + 1}) has '${c.existing}' but new word has '${c.new}'`).join("; ")}`;
                    handleSnackbar(msg, "error");
                    continue;
                }

                const newWordEntry = { id: uuidv4(), text: displayWord, direction, length: gridWord.length, reversed, positions };
                addedWords.push(newWordEntry);
                filledSoFar = [...filledSoFar, newWordEntry];
                await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, filledSoFar, gridWord);
            }

            if (addedWords.length === 0) {
                if (skippedNoPositions > 0) {
                    handleSnackbar(`${skippedNoPositions} word(s) skipped (no position data).`, "warning");
                }
                setLoadingFillAdd(false);
                return;
            }
            const wordList = addedWords.map((e) => e.text).filter(Boolean).join(", ");
            const count = addedWords.length;
            const msg = count === 1 ? `"${wordList}" added successfully!` : `${count} words added: ${wordList}`;
            const skipMsg = skippedNoPositions > 0 ? ` ${skippedNoPositions} word(s) skipped (no position data).` : "";
            handleSnackbar(msg + skipMsg, skippedNoPositions > 0 ? "warning" : "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error adding words from cart:", err);
            handleSnackbar(err?.message ?? "Failed to add words. Please try again.", "error");
        } finally {
            setLoadingFillAdd(false);
        }
    };

    const handleNotInUsePlaceWord = (word) => {
        if (!word) return;
        setPlacementWordFromFill(word);
    };

    const handleRearrangeWords = async () => {
        if (!gridTpl?.id) return;
        setLoadingRearrange(true);
        try {
            await AdminGridBoardService.rearrangeWords(gridTpl.id);
            handleSnackbar("Words rearranged successfully", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error rearranging words:", err);
            handleSnackbar(err?.message || "Failed to rearrange words. Please try again.", "error");
        } finally {
            setLoadingRearrange(false);
        }
    };

    const handleReadyForReview = () => {
        if (gridTpl?.state === "REVIEWED") {
            setReadyToReviewDialogOpen(true);
        } else {
            setPuzzleTypeDialogOpen(true);
        }
    };

    const confirmReadyToReview = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingReadyToReview(true);
            await CreatorGridService.updateGridState(gridTpl.id, "READY_TO_REVIEW");
            handleSnackbar("Puzzle moved back to Ready to Review", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving puzzle to Ready to Review:", err);
            handleSnackbar(err?.message || "Failed to update state", "error");
        } finally {
            setLoadingReadyToReview(false);
            setReadyToReviewDialogOpen(false);
        }
    };

    const handleReviewedClick = () => {
        if (gridTpl?.state === PUBLISHED) setBackToReviewedDialogOpen(true);
        else setReviewedDialogOpen(true);
    };

    const handleReviewedDialogClose = () => {
        setReviewedDialogOpen(false);
    };

    const handleDraftClick = () => setDraftDialogOpen(true);

    const confirmDraft = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingDraft(true);
            await CreatorGridService.updateGridState(gridTpl.id, "DRAFT");
            handleSnackbar("Puzzle moved back to Draft", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error moving puzzle to Draft:", err);
            handleSnackbar(err?.message || "Failed to update state to Draft", "error");
        } finally {
            setLoadingDraft(false);
            setDraftDialogOpen(false);
        }
    };

    const confirmReviewed = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingReviewed(true);
            await CreatorGridService.updateGridState(gridTpl.id, "REVIEWED");
            handleSnackbar("Puzzle marked as Reviewed", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error marking puzzle as Reviewed:", err);
            handleSnackbar(err?.message || "Failed to update state to Reviewed", "error");
        } finally {
            setLoadingReviewed(false);
            setReviewedDialogOpen(false);
        }
    };

    const handlePublishClick = () => setPublishDialogOpen(true);

    const confirmPublish = async () => {
        if (!gridTpl?.id) return;
        try {
            setLoadingPublished(true);
            await CreatorGridService.updateGridState(gridTpl.id, "PUBLISHED");
            handleSnackbar("Puzzle published successfully", "success");
            setRefresh((prev) => prev + 1);
        } catch (err) {
            Logger.error("Error publishing puzzle:", err);
            handleSnackbar(err?.message || "Failed to publish puzzle", "error");
        } finally {
            setLoadingPublished(false);
            setPublishDialogOpen(false);
        }
    };

    const handleStateBack = () => {
        const s = gridTpl?.state;
        if (s === READY_TO_REVIEW) handleDraftClick();
        else if (s === REVIEWED) setReadyToReviewDialogOpen(true);
        else if (s === PUBLISHED) setBackToReviewedDialogOpen(true);
    };

    const confirmBackToReviewed = async () => {
        if (!gridTpl?.id) return;
        setLoadingBackToReviewed(true);
        try {
            const result = await CreatorGridService.updateGridState(gridTpl.id, "REVIEWED");
            if (result?.code === "FAILURE") {
                handleSnackbar(result.message || "Failed to move back to Reviewed", "error");
            } else {
                handleSnackbar("Puzzle moved back to Reviewed", "success");
                setRefresh((prev) => prev + 1);
                setBackToReviewedDialogOpen(false);
            }
        } catch (err) {
            Logger.error("Error moving back to Reviewed:", err);
            handleSnackbar(err?.message || "Failed to update state", "error");
        } finally {
            setLoadingBackToReviewed(false);
            setBackToReviewedDialogOpen(false);
        }
    };

    const handleStateForward = () => {
        const s = gridTpl?.state;
        if (s === DRAFT) handleReadyForReview();
        else if (s === READY_TO_REVIEW && isManager) handleReviewedClick();
        else if (s === REVIEWED) handlePublishClick();
    };

    const handlePuzzleTypeSelected = async (puzzleType) => {
        try {
            const result = await AdminGridBoardService.updateHwTplState(gridTpl.id, READY_TO_REVIEW, puzzleType);
            if (result && (result.code === "SUCCESS" || !result.code)) {
                handleSnackbar("Puzzle moved to review stage successfully", "success");
                navigate('/creator/grid');
            } else {
                handleSnackbar(result?.message || "Failed to update puzzle state", "error");
            }
        } catch (error) {
            Logger.error("Error moving puzzle to review stage:", error);
            handleSnackbar(error?.message || "Error moving puzzle to review stage", "error");
        }
    };

    const handleFetchWordScores = async () => {
        if (!gridTpl?.wordsInUse) {
            handleSnackbar("No words found in puzzle", "warning");
            return;
        }
        try {
            setLoadingScores(true);
            if (!scoreDialogOpen) setScoreDialogOpen(true);
            const words = gridTpl.wordsInUse.split('\n').map(w => w.trim()).filter(w => w.length > 0);
            if (words.length === 0) {
                handleSnackbar("No words found in puzzle", "warning");
                setScoreDialogOpen(false);
                return;
            }
            const queryParams = words.map(word => `words=${encodeURIComponent(word)}`).join('&');
            const language = gridTpl?.language || 'english';
            const url = `/home/dic/${language}/multiple?validOnly=false&${queryParams}`;
            const result = await ApiService.getData(url, null);
            if (result && Array.isArray(result)) {
                setWordScores(result);
            } else {
                handleSnackbar("Failed to fetch word scores", "error");
                setWordScores([]);
            }
        } catch (error) {
            Logger.error("Error fetching word scores:", error);
            handleSnackbar("Error fetching word scores", "error");
            setWordScores([]);
        } finally {
            setLoadingScores(false);
        }
    };

    const handleGenerateWordNetwork = async () => {
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        try {
            if (!gridTpl?.id) {
                handleSnackbar("No puzzle ID available", "error");
                return;
            }
            const result = await AdminGridBoardService.generateWordNetwork(gridTpl.id);
            if (result?.code === "SUCCESS") {
                handleSnackbar("Word network generated successfully", "success");
                setRefresh(prev => prev + 1);
            } else {
                handleSnackbar("Failed to generate word network", "error");
            }
        } catch (error) {
            Logger.error("Error generating word network:", error);
            handleSnackbar("Error generating word network", "error");
        } finally {
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleRefreshWordNetwork = async () => {
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';
        try {
            if (!gridTpl?.id) {
                handleSnackbar("No puzzle ID available", "error");
                return;
            }
            const language = gridTpl?.language || 'english';
            const result = await AdminGridBoardService.refreshWordNetwork(gridTpl.id, language);
            if (result?.code === "SUCCESS") {
                handleSnackbar("Word network refreshed successfully", "success");
                setRefresh(prev => prev + 1);
            } else {
                handleSnackbar("Failed to refresh word network", "error");
            }
        } catch (error) {
            Logger.error("Error refreshing word network:", error);
            handleSnackbar("Error refreshing word network", "error");
        } finally {
            document.body.style.cursor = originalCursor || 'default';
        }
    };

    const handleSaveTitle = (definition, word) => {
        if (!definition || !word) return;
        setEditingDefinition(definition);
        setEditingWord(word);
        setOpenWordRedefineDialog(true);
    };

    const handleWordRedefineSave = async (editedDefinition, saveAsHiddenMessage = false) => {
        try {
            await AdminGridBoardService.setClue(editingWord, editedDefinition, gridTpl.id, saveAsHiddenMessage);
            handleSnackbar("Definition saved successfully", "success");
            setOpenWordRedefineDialog(false);
            setEditingDefinition("");
            setEditingWord("");
            setRefresh(prev => prev + 1);
        } catch (error) {
            Logger.error("Error saving definition:", error);
            handleSnackbar("Error saving definition", "error");
        }
    };

    const handleWordRedefineCancel = () => {
        setOpenWordRedefineDialog(false);
        setEditingDefinition("");
        setEditingWord("");
    };

    const handleWordsInPuzzleClick = (item) => {
        setSelectedWord({ word: item.text, text: item.text, positions: item.positions || [] });
        setSelectedAssociatedWord(null);
        setSelectedManualWord(null);
        setSelectedNetworkWord(null);
    };

    const hideMiddlePanel = true; // Creator: do not show Association Search middle panel
    const rightPanelSize = hideMiddlePanel ? 8 : 4;

    if (loading) {
        return (
            <Box sx={{ p: 0 }}>
                <Typography sx={{ mt: 4 }}>Loading...</Typography>
            </Box>
        );
    }

    if (fetchError || !gridTpl) {
        return (
            <Box sx={{ p: 0 }}>
                <Typography color="error">
                    {fetchError || "Puzzle not found"}
                </Typography>
            </Box>
        );
    }

    const wordsInPuzzle = filledCoordinates
        .filter((c) => c.text && typeof c.text === "string")
        .map((c) => ({ text: c.text.trim(), isScattered: c.direction === "scattered", positions: c.positions || [] }))
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: "base" }));

    return (
        <Box sx={{ p: 0 }}>
            <BorderHeader
                onBack={handleBack}
                puzzle={gridTpl}
                puzzleType="grid"
                hiddenMessage={scatteredWord}
            />

            <Grid container spacing={2} sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>
                            Puzzle Preview
                        </Typography>
                        <Tooltip title={showAllWordsColored ? "Hide word colors" : "Show all words with color"}>
                            <IconButton
                                size="small"
                                onClick={() => setShowAllWordsColored((v) => !v)}
                                color={showAllWordsColored ? "primary" : "default"}
                                aria-label={showAllWordsColored ? "Hide word colors" : "Show all words with color"}
                            >
                                {showAllWordsColored ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box
                        ref={gridContainerRef}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1,
                            backgroundColor: "#fafafa",
                            overflow: "hidden",
                            height: "350px",
                            mb: 2
                        }}
                    >
                        <Box
                            ref={gridContentRef}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                height: "100%",
                                transform: `scale(${gridScale})`,
                                transformOrigin: "center center"
                            }}
                        >
                            {square.length > 0 ? (
                                <Box>
                                    <Box sx={{ display: 'flex', gap: '1px', mb: 0.5 }}>
                                        <Box sx={{ width: 24, height: 24 }} />
                                        {Array.from({ length: 12 }, (_, colIndex) => (
                                            <Box key={colIndex} sx={{ width: 24, height: 24, border: '1px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold', color: 'primary.main' }}>
                                                {colIndex}
                                            </Box>
                                        ))}
                                    </Box>
                                    {square.map((row, rowIndex) => (
                                        <Box key={rowIndex} sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                                            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 'bold', color: 'primary.main' }}>
                                                {rowIndex}
                                            </Box>
                                            <MiniPuzRow
                                                rowIndex={rowIndex}
                                                row={row}
                                                hiddenWord={!allWordsPositionColors && ((selectedWord?.word || selectedWord?.text || selectedManualWord?.word || selectedAssociatedWord?.word || selectedAssociatedWord?.text) || (selectedManualWord?.startRow != null && selectedManualWord?.startCol != null ? " " : ""))}
                                                positions={(() => {
                                                    const base = (selectedWord?.positions || selectedManualWord?.positions || selectedAssociatedWord?.positions) || [];
                                                    const sel = selectedManualWord?.startRow != null && selectedManualWord?.startCol != null ? { row: selectedManualWord.startRow, col: selectedManualWord.startCol } : null;
                                                    if (sel && !base.some(p => p.row === sel.row && p.col === sel.col)) return [...base, sel];
                                                    return base;
                                                })()}
                                                positionColors={allWordsPositionColors}
                                                onCellClick={handleCellClick}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography color="text.secondary" sx={{ p: 2 }}>No puzzle grid data available</Typography>
                            )}
                        </Box>
                    </Box>
                    {cellsFullyFilled && (
                        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                            All cells are filled. Fill and Rearrange are disabled.
                        </Alert>
                    )}

                    {gridTpl?.state && gridTpl.state !== '' && !['DISCARDED', 'DISABLED'].includes(gridTpl.state) && (() => {
                        const actionsDisabled = ['REVIEWED', 'PUBLISHED', 'DISCARDED', 'DISABLED'].includes(gridTpl?.state);
                        const statePastReview = ['PUBLISHED', 'DISCARDED', 'DISABLED'].includes(gridTpl?.state);
                        return (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Tooltip title="Validate puzzle. Use Resolve to fix conflicts, duplicates, and mismatches.">
                                        <Button variant="outlined" fullWidth startIcon={<FactCheckIcon />} onClick={() => setValidationDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }} size="small">
                                            Validation
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Tooltip title="View word scores">
                                        <Button variant="outlined" fullWidth startIcon={loadingScores ? <CircularProgress size={16} color="inherit" /> : <ScoreIcon />} onClick={handleFetchWordScores} disabled={loadingScores} sx={{ color: '#ed6c02', borderColor: '#ed6c02', '&:hover': { backgroundColor: '#ed6c02', color: 'white', borderColor: '#ed6c02' }, whiteSpace: 'nowrap' }} size="small">
                                            Score
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Button variant="outlined" fullWidth startIcon={<OpenInNewIcon />} onClick={() => setNetworkDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }} size="small">
                                        Network
                                    </Button>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Tooltip title={cellsFullyFilled ? "Grid is full—no empty cells to fill" : "Open Fill dialog (associations, candidate words, match keyword)"}>
                                        <span>
                                            <Button variant="outlined" fullWidth startIcon={<Tooltip title="Bedrock AI"><span style={{ display: "inline-flex" }}><AutoAwesomeIcon /></span></Tooltip>} onClick={() => setFillDialogOpen(true)} disabled={gridTpl?.state !== DRAFT || actionsDisabled || cellsFullyFilled || loadingApplyTitleHook} sx={{ color: '#1976d2', borderColor: '#1976d2', '&:hover': { backgroundColor: '#1976d2', color: 'white', borderColor: '#1976d2' }, whiteSpace: 'nowrap' }} size="small">
                                                Fill
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Tooltip title={cellsFullyFilled ? "Grid is full—no empty cells to rearrange" : "Rearrange words in the puzzle"}>
                                        <span>
                                            <Button variant="outlined" fullWidth startIcon={loadingRearrange ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />} onClick={handleRearrangeWords} disabled={gridTpl?.state !== DRAFT || actionsDisabled || cellsFullyFilled || loadingApplyTitleHook || loadingFillAdd || loadingRearrange} sx={{ color: '#1976d2', borderColor: '#1976d2', '&:hover': { backgroundColor: '#1976d2', color: 'white', borderColor: '#1976d2' }, whiteSpace: 'nowrap' }} size="small">
                                                Rearrange
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {!isCreator && (
                                    <Tooltip title="Go to previous state">
                                        <span>
                                            <IconButton onClick={handleStateBack} disabled={gridTpl?.state === DRAFT || loadingApplyTitleHook || loadingFillAdd || loadingDraft || loadingReadyToReview || loadingReviewed || loadingPublished || loadingBackToReviewed} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                                <ArrowBackIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                                <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', minWidth: 0 }}>
                                    <Button variant="outlined" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handleDraftClick} startIcon={loadingDraft ? <CircularProgress size={16} color="inherit" /> : <ArrowBackIcon />} disabled={gridTpl?.state !== READY_TO_REVIEW || loadingApplyTitleHook || loadingFillAdd || loadingDraft}>
                                        Draft
                                    </Button>
                                    <Button variant="outlined" color="success" sx={{ minWidth: 120, whiteSpace: 'nowrap' }} size="small" onClick={handleReadyForReview} startIcon={<CheckCircleIcon />} disabled={(gridTpl?.state !== DRAFT && gridTpl?.state !== REVIEWED) || loadingApplyTitleHook || loadingFillAdd}>
                                        Ready to Review
                                    </Button>
                                    {!isCreator && (
                                        <>
                                            <Button variant="outlined" color="primary" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handleReviewedClick} startIcon={loadingReviewed || loadingBackToReviewed ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />} disabled={((gridTpl?.state !== READY_TO_REVIEW || !isManager) && gridTpl?.state !== PUBLISHED) || loadingApplyTitleHook || loadingFillAdd || loadingReviewed || loadingBackToReviewed}>
                                                Reviewed
                                            </Button>
                                            <Button variant="outlined" color="secondary" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={handlePublishClick} startIcon={loadingPublished ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />} disabled={gridTpl?.state !== REVIEWED || loadingApplyTitleHook || loadingFillAdd || loadingPublished}>
                                                Published
                                            </Button>
                                            <Button variant="outlined" color="error" sx={{ minWidth: 90, whiteSpace: 'nowrap' }} size="small" onClick={() => { setWordToDelete(null); setDiscardDialogOpen(true); }} startIcon={<DeleteOutlineIcon />} disabled={loadingApplyTitleHook || loadingFillAdd}>
                                                Discard
                                            </Button>
                                        </>
                                    )}
                                </Box>
                                {!isCreator && (
                                    <Tooltip title="Go to next state">
                                        <span>
                                            <IconButton onClick={handleStateForward} disabled={(gridTpl?.state === PUBLISHED) || (gridTpl?.state === READY_TO_REVIEW && !isManager) || loadingApplyTitleHook || loadingFillAdd || loadingDraft || loadingReadyToReview || loadingReviewed || loadingPublished || loadingBackToReviewed} size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
                                                <ArrowForwardIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {gridTpl?.state === "REVIEWED" && (
                                <Alert severity="info" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <span>
                                        This puzzle is <strong>REVIEWED</strong>. It will be published on schedule.
                                    </span>
                                </Alert>
                            )}
                        </Box>
                        );
                    })()}
                </Grid>

                {(!hideMiddlePanel || (placementWordFromFill && gridTpl?.type === "HIDDEN_MESSAGE")) && (
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: (hideMiddlePanel && !(placementWordFromFill && gridTpl?.type === "HIDDEN_MESSAGE")) ? "none" : "flex", flexDirection: "column", height: "100%" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#3BA666" }}>
                                {modeRef.current === "assoc" ? "Association Search" : modeRef.current === "ai" ? "AI Associations" : "Manual Entry"}
                            </Typography>
                            <ToggleButtonGroup value={modeRef.current} exclusive onChange={handleModeChange} size="small">
                                <ToggleButton value="assoc">Assoc</ToggleButton>
                                <ToggleButton value="manual">Manual</ToggleButton>
                                <ToggleButton value="ai">AI</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {(modeRef.current === "assoc" || placementWordFromFill) ? (
                            <GridCanAssoc gridTpl={gridTpl} square={square} onWordAdd={handleWordAdd} onShowSnackbar={handleSnackbar} onWordSelect={handleWordSelect} onRefresh={() => setRefresh((prev) => prev + 1)} highlightedWordFromPreview={(selectedWord?.text ?? selectedWord?.word) || null} placementWordFromFill={placementWordFromFill} onClearPlacementWordFromFill={() => setPlacementWordFromFill(null)} />
                        ) : modeRef.current === "ai" ? (
                            <GridCandAI onWordAdd={handleManualWordAdd} onWordSelect={handleManualWordSelect} selectedManualWord={selectedManualWord} onShowSnackbar={handleSnackbar} square={square} gridTpl={gridTpl} onRefresh={() => setRefresh((prev) => prev + 1)} />
                        ) : (
                            <GridCandManual onWordAdd={handleManualWordAdd} onWordSelect={handleManualWordSelect} selectedManualWord={selectedManualWord} onShowSnackbar={handleSnackbar} square={square} gridTpl={gridTpl} />
                        )}
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: rightPanelSize }} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    {hideMiddlePanel && wordsInPuzzle.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>Words in puzzle</Typography>
                                <Tooltip title="Delete selected word from puzzle">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const wordText = selectedWord?.text || selectedWord?.word;
                                                if (wordText) {
                                                    setWordToDelete(wordText);
                                                    setDiscardDialogOpen(true);
                                                }
                                            }}
                                            disabled={!(selectedWord?.text || selectedWord?.word)}
                                            sx={{ '&:disabled': { opacity: 0.3 } }}
                                        >
                                            <DeleteIcon fontSize="small" color={(selectedWord?.text || selectedWord?.word) ? "error" : "action"} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {wordsInPuzzle.map((item, idx) => {
                                    const displayedWord = (selectedWord?.text || selectedWord?.word || selectedAssociatedWord?.text || selectedAssociatedWord?.word || selectedManualWord?.word || selectedNetworkWord) || "";
                                    const isSelected = displayedWord.toLowerCase() === item.text.toLowerCase();
                                    return (
                                        <Chip
                                            key={idx}
                                            label={item.isScattered ? `"${item.text}" (hidden)` : item.text}
                                            size="small"
                                            variant={isSelected ? "filled" : (item.isScattered ? "filled" : "outlined")}
                                            color={isSelected ? "primary" : (item.isScattered ? "secondary" : "default")}
                                            onClick={() => handleWordsInPuzzleClick(item)}
                                            sx={{
                                                cursor: 'pointer',
                                                ...(isSelected && {
                                                    fontWeight: 700,
                                                    boxShadow: 2,
                                                    border: '2px solid',
                                                    borderColor: 'primary.main'
                                                })
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                    {hideMiddlePanel && emptyCellCount > 0 && (
                        <GridFillNotInUsePanel
                            wordsNotInUse={gridTpl?.wordsNotInUse ? gridTpl.wordsNotInUse.split("\n").map((w) => w.trim()).filter(Boolean) : []}
                            selectedWord={selectedWord?.text || selectedWord?.word || null}
                            onSelectWord={(word) => handleWordSelect({ text: word })}
                            onPlaceWord={handleNotInUsePlaceWord}
                            onAddAllWords={async () => {
                                if (!gridTpl?.id) return;
                                try {
                                    const result = await AdminGridBoardService.fillMore(gridTpl.id);
                                    setRefresh((prev) => prev + 1);
                                    const wordsPlaced = result?.wordsPlaced ?? result?.addedCount ?? result?.wordsAdded ?? 0;
                                    if (wordsPlaced > 0) {
                                        handleSnackbar(`${wordsPlaced} word(s) added to puzzle`, "success");
                                    } else {
                                        handleSnackbar(result?.message ?? "No additional words placed", "info");
                                    }
                                } catch (err) {
                                    Logger.error("Error fill-more:", err);
                                    handleSnackbar("Error adding words: " + (err?.message || err), "error");
                                }
                            }}
                            gridId={gridTpl?.id}
                            emptyCellCount={emptyCellCount}
                            onShowSnackbar={handleSnackbar}
                        />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3BA666' }}>Word Meaning</Typography>
                        <Tooltip title="View Banned Words">
                            <IconButton size="small" onClick={() => setOpenBannedWordsPopup(true)} sx={{ color: 'error.main' }}>
                                <BlockIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {(selectedWord || selectedManualWord || selectedAssociatedWord || selectedNetworkWord) ? (
                            <WordMeaning selectedWord={(selectedNetworkWord || selectedWord?.word || selectedWord?.text || selectedManualWord?.word || selectedAssociatedWord?.word) || ""} onSaveDefinition={handleSaveTitle} gridTplId={gridTpl?.id} language={gridTpl?.language} fullHeight={true} />
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                Select a word from the puzzle, words not in puzzle, or word network to see its meaning
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} message={snackbarMessage} severity={snackbarSeverity} />

            <ConfirmDialog open={discardDialogOpen} setOpen={(open) => { setDiscardDialogOpen(open); if (!open) setWordToDelete(null); }} title={wordToDelete ? "Delete Word" : "Discard Puzzle"} message={wordToDelete ? `Would you like to delete the word "${wordToDelete}"?` : "Would you like to discard this puzzle?"} targetName="" targetId="" callback={wordToDelete ? handleConfirmDeleteWord : handleDiscardPuzzle} />

            <ConfirmDialog
                open={disableDialogOpen}
                setOpen={setDisableDialogOpen}
                title="Disable Puzzle"
                message="Would you like to disable this puzzle? It will be withdrawn from publication."
                warningAlert="If the puzzle is set to 'DISABLED' now, the puzzle will be disappeared on all user's page 1 week later."
                targetName=""
                targetId=""
                callback={handleDisablePuzzle}
            />

            <ConfirmDialog
                open={publishDialogOpen}
                setOpen={setPublishDialogOpen}
                title="Publish Puzzle"
                message="Would you like to publish this puzzle now?"
                extraContent={
                    <Alert severity="info" sx={{ mt: 2 }}>
                        The system publishes puzzles by schedule automatically. If you want to publish this puzzle now, click Yes.
                    </Alert>
                }
                targetName=""
                targetId=""
                callback={confirmPublish}
            />

            <ConfirmDialog
                open={backToReviewedDialogOpen}
                setOpen={setBackToReviewedDialogOpen}
                title="Move back to Reviewed"
                message="Would you like to move this puzzle back to Reviewed state?"
                warningAlert={
                    <>
                        If this puzzle is played 1 time or more, it can&apos;t be moved back to &quot;REVIEWED&quot;. It could be &quot;DISABLED&quot; state, request it to Young.
                        <br />
                        <br />
                        If the puzzle is set to &quot;DISABLED&quot; now, the puzzle will be disappeared on all user&apos;s page 1 week later.
                    </>
                }
                targetName=""
                targetId=""
                callback={confirmBackToReviewed}
            />

            <VerifyDialog
                open={puzzleTypeDialogOpen}
                onClose={() => setPuzzleTypeDialogOpen(false)}
                puzzleId={gridTpl?.id}
                requestPuzzle={AdminGridBoardService.requestHwTpl}
                defaultPuzzleType="WORD_SEARCH"
                allowedPuzzleTypes={['WORD_SEARCH', 'HIDDEN_MESSAGE']}
                onPuzzleTypeSelected={handlePuzzleTypeSelected}
                onShowSnackbar={handleSnackbar}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!gridTpl?.id) return;
                    await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, null);
                    setRefresh((prev) => prev + 1);
                }}
            />

            <BannedWordsPopup open={openBannedWordsPopup} onClose={() => setOpenBannedWordsPopup(false)} language={gridTpl?.language || "english"} />

            <AddBannedWordDialog
                open={openAddBannedWordDialog}
                onClose={() => { setOpenAddBannedWordDialog(false); setSelectedWordForBanned(""); }}
                onAdd={async (word, category, wordLanguage) => {
                    setLoadingAddBanned(true);
                    try {
                        await BannedService.addWord(category, word, "", wordLanguage);
                        handleSnackbar(`"${word}" added to banned words`, "success");
                        setOpenAddBannedWordDialog(false);
                        setSelectedWordForBanned("");
                    } catch (err) {
                        Logger.error("Error adding banned word:", err);
                        handleSnackbar(err?.message ?? "Failed to add banned word", "error");
                    } finally {
                        setLoadingAddBanned(false);
                    }
                }}
                selectedWord={selectedWordForBanned}
                language={gridTpl?.language ?? "english"}
                loading={loadingAddBanned}
            />

            <WordRedefine open={openWordRedefineDialog} onClose={handleWordRedefineCancel} onSave={handleWordRedefineSave} word={editingWord} definition={editingDefinition} hiddenMessage={scatteredWord?.text} />

            <GridFillDialog
                open={fillDialogOpen}
                onClose={() => setFillDialogOpen(false)}
                title="Fill"
                puzzleTitle={gridTpl?.title}
                puzzleType={gridTpl?.type}
                tags={gridTpl?.tags ?? gridTpl?.template?.tags ?? []}
                gridId={gridTpl?.id}
                language={gridTpl?.language ?? "english"}
                cellsFullyFilled={cellsFullyFilled}
                emptyCellCount={emptyCellCount}
                onPlaceWords={handlePlaceWords}
                onPlaceWord={handleFillPlaceWord}
                onTab2Add={handleFillTab2Add}
                onNotInUsePlaceWord={handleNotInUsePlaceWord}
                adding={loadingFillAdd}
                wordsNotInUse={gridTpl?.wordsNotInUse ? gridTpl.wordsNotInUse.split("\n").map((w) => w.trim()).filter(Boolean) : []}
                onReadyToReview={() => {
                    setFillDialogOpen(false);
                    handleReadyForReview();
                }}
                readyToReviewDisabled={(gridTpl?.state !== DRAFT && gridTpl?.state !== REVIEWED) || loadingApplyTitleHook || loadingFillAdd}
                readyToReviewLoading={loadingReadyToReview}
                onAddBannedWord={(word) => {
                    if (!word?.trim()) return;
                    setSelectedWordForBanned(word.trim());
                    setOpenAddBannedWordDialog(true);
                }}
                filledCoordinates={filledCoordinates}
                onShowSnackbar={handleSnackbar}
                onFillMoreSuccess={() => setRefresh((prev) => prev + 1)}
            />

            <ConfirmDialog open={draftDialogOpen} setOpen={setDraftDialogOpen} title="Move back to Draft?" message="Change puzzle state to Draft? You can edit the puzzle again." callback={confirmDraft} />

            <ConfirmDialog open={readyToReviewDialogOpen} setOpen={setReadyToReviewDialogOpen} title="Move back to Ready to Review?" message="Change puzzle state from Reviewed to Ready to Review?" callback={confirmReadyToReview} />

            <MarkAsReviewedDialog
                open={reviewedDialogOpen}
                onClose={handleReviewedDialogClose}
                onConfirm={confirmReviewed}
                gridTpl={gridTpl}
                loadingReviewed={loadingReviewed}
            />

            <ValidationDialog
                open={validationDialogOpen}
                onClose={() => setValidationDialogOpen(false)}
                onResolve={(resolveType) => {
                    setValidationDialogOpen(false);
                    setResolveDialogType(resolveType);
                    setResolveDialogOpen(true);
                }}
                gridTpl={gridTpl}
            />

            <ResolvePuzzleDialog
                open={resolveDialogOpen}
                onClose={() => setResolveDialogOpen(false)}
                gridTpl={gridTpl}
                resolveType={resolveDialogType}
                onRemoveWords={async (updatedFilledCoordinates) => {
                    if (!gridTpl?.id) return;
                    await AdminGridBoardService.updatePuzzleCoordinates(gridTpl.id, updatedFilledCoordinates, null);
                }}
                onRefresh={() => setRefresh((prev) => prev + 1)}
                onShowSnackbar={handleSnackbar}
            />

            <ConfirmDialog open={showTitleHookDialog} setOpen={setShowTitleHookDialog} title="Hook the Title" message={<Box><span>Ready to apply a title hook based on words in the puzzle? <strong>Recommended to change the title</strong> - the AI tool will make it attractive.</span>{gridTpl?.type === "HIDDEN_MESSAGE" && !scatteredWord?.text && (<Alert severity="warning" sx={{ mt: 2 }}>"Hidden Message" type puzzle should have the message</Alert>)}</Box>} callback={confirmApplyTitleHook} />

            <WordScoreDialog open={scoreDialogOpen} onClose={() => setScoreDialogOpen(false)} wordScores={wordScores} loadingScores={loadingScores} language={gridTpl?.language || "english"} onRefresh={async () => { setScoreDialogOpen(false); setTimeout(async () => { setScoreDialogOpen(true); await handleFetchWordScores(); }, 100); }} />

            <WordNetworkDialog open={networkDialogOpen} onClose={() => setNetworkDialogOpen(false)} hasWordNetwork={hasWordNetwork} wordNetworkData={wordNetworkData} onDeleteWord={handleDeleteWordFromNetwork} onNodeSelect={(word) => { setSelectedWord(null); setSelectedAssociatedWord(null); setSelectedManualWord(null); setSelectedNetworkWord(word); }} onRefresh={handleRefreshWordNetwork} onGenerateWordNetwork={handleGenerateWordNetwork} />
        </Box>
    );
}
